// La card de `mostrar_guardado`: una galería de SOLO LECTURA con lo que el usuario ya
// tiene — ADR-2026-09-08-mostrar-lo-guardado.
//
// Misma mecánica que `RecentMediaCard`: se AUTO-RESUELVE de inmediato (no espera el fetch)
// para que aparezca en el chat y no quede colgada en el slot de abajo, y después enriquece
// su `response` con lo que mostró, que es lo que el mecanismo `[registro:]` lee en el turno
// siguiente para mapear "el segundo" a un id.
//
// No cuesta créditos y no genera nada. Al tocar una miniatura se va al lugar donde esa cosa
// vive de verdad (el producto a Mi marca); las que no tienen pantalla propia no navegan.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Download, ImageOff, Loader2 } from 'lucide-react';
import CachedImg from '../../components/CachedImg';
import { supabase } from '../../lib/supabase';
import { signReferenceImage } from './agentAttachments';
import { useAgentEnv } from './agentEnvContext';
import { fetchFondosGuardados, signFondoGuardadoFull } from './fondosApi';
import { useUserModels } from './modelsContext';
import {
  busquedaPedida,
  ETIQUETA_TIPO,
  TIPOS_GUARDADO,
  filtrarGuardado,
  nombreDeArchivo,
  nombreDelZip,
  puedeDescargarTodo,
  type ItemGuardado,
  type TipoGuardado,
} from './mostrarGuardado';
import { fetchMyCustomPoses } from './posesCustomApi';
import { getProductFullImageUrl } from './productsApi';
import { useUserProducts } from './productsContext';
import { descargarComoZip, descargarSueltos, type ArchivoDeLote } from '../../utils/descargarLote';
import { useOpcionesDeDescarga } from '../../components/descarga/useOpcionesDeDescarga';
import { mapWithConcurrency } from '../../utils/concurrency';
import type { UseV2ChatReturn, V2ToolCard } from './useV2Chat';

/** Bucket y carpeta donde `uploadReferenceImage` deja las fotos que el usuario adjunta al
 *  chat. No tienen fila en ninguna tabla (son adjuntos efímeros), así que la única lista
 *  posible es la del storage. */
const BUCKET_ADJUNTOS = 'video-project-assets';
const CARPETA_ADJUNTOS = 'agent-refs';

const cuandoFue = (iso: string | undefined): string => {
  if (!iso) return '';
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (!Number.isFinite(dias) || dias < 0) return '';
  if (dias === 0) return 'hoy';
  if (dias === 1) return 'ayer';
  if (dias < 30) return `hace ${dias} días`;
  const meses = Math.floor(dias / 30);
  return meses === 1 ? 'hace un mes' : `hace ${meses} meses`;
};

/** Las fotos que subió al chat, del storage. Sin fila en base no hay nombre: la etiqueta
 *  es cuándo la subió, que es lo único que le sirve para reconocerla. */
async function fotosSubidas(companyId: string, limite: number): Promise<ItemGuardado[]> {
  const { data, error } = await supabase.storage
    .from(BUCKET_ADJUNTOS)
    .list(`${companyId}/${CARPETA_ADJUNTOS}`, {
      limit: limite,
      sortBy: { column: 'created_at', order: 'desc' },
    });
  if (error || !data) return [];
  const archivos = data.filter((f) => f.name && !f.name.startsWith('.'));
  const firmadas = await Promise.all(
    archivos.map(async (f) => {
      // Se firma sin transformar, así que la misma URL sirve para ver y para descargar.
      const url =
        (await signReferenceImage(
          `${companyId}/${CARPETA_ADJUNTOS}/${f.name}`,
          BUCKET_ADJUNTOS,
        )) || '';
      return {
        id: f.name,
        label: cuandoFue(f.created_at as string | undefined),
        thumbUrl: url,
        fullUrl: url,
      };
    }),
  );
  return firmadas.filter((f) => f.thumbUrl);
}

export const MostrarGuardadoCard: React.FC<{
  card: V2ToolCard;
  chat: UseV2ChatReturn;
  /** `null` = el modelo mandó un tipo que no existe. La card se dibuja igual y se
   *  resuelve con el motivo: si no se dibujara, nadie resolvería el turno. */
  tipo: TipoGuardado | null;
  limite: number;
}> = ({ card, chat, tipo, limite }) => {
  const navigate = useNavigate();
  const { companyId } = useAgentEnv();
  const productos = useUserProducts();
  const modelos = useUserModels();
  const buscar = busquedaPedida(card.input);

  const [cargando, setCargando] = useState(true);
  const [items, setItems] = useState<ItemGuardado[]>([]);
  const [hayEnTotal, setHayEnTotal] = useState(false);
  const resueltoRef = useRef(false);

  // Resolver YA: la card se renderiza en el chat y no traba el turno. Con un tipo
  // inválido se resuelve igual, con el motivo, para que el agente lo corrija en su
  // próximo turno en vez de dejar la conversación colgada.
  useEffect(() => {
    if (!resueltoRef.current && card.status === 'pending') {
      resueltoRef.current = true;
      chat.resolveToolCard(
        card.id,
        tipo
          ? { shown: true, tipo }
          : {
              shown: false,
              motivo: 'tipo_invalido',
              tipos_validos: TIPOS_GUARDADO,
            },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Depende de `companyId`: al volver al chat tras navegar arranca en null y carga async;
  // sin la dependencia el fetch corría con null y la galería quedaba vacía para siempre.
  useEffect(() => {
    if (!tipo) {
      setCargando(false);
      return;
    }
    let vivo = true;
    setCargando(true);
    (async () => {
      let todos: ItemGuardado[] = [];
      try {
        if (tipo === 'productos') {
          // Sin `fullUrl`: la foto grande del producto se firma recién al descargar (el
          // `imageUrl` del catálogo es una miniatura de 300px).
          todos = productos.map((p) => ({
            id: String(p.id),
            label: p.name,
            thumbUrl: p.imageUrl || '',
          }));
        } else if (tipo === 'modelos') {
          // Sólo los PROPIOS: el picker mezcla los globales de Atlas, pero "mis modelos
          // guardados" son los de la empresa. Mismo criterio que poses y fondos, que ya
          // traen sólo lo del cliente.
          todos = modelos
            .filter((m) => m.idCompany)
            .map((m) => ({
              id: String(m.id),
              label: m.nombre,
              thumbUrl: m.thumbUrl || m.thumbnailUrl || '',
              // `thumbnailUrl` es FULL RES (lo dice su propio tipo); `thumbUrl` es la
              // transformación chica para pickers.
              fullUrl: m.thumbnailUrl || '',
            }));
        } else if (tipo === 'poses' && companyId) {
          todos = (await fetchMyCustomPoses(companyId)).map((p) => ({
            id: String(p.id),
            label: p.nombre,
            thumbUrl: p.imageUrl || '',
            // La pose se firma sin transformar: la miniatura YA es el original.
            fullUrl: p.imageUrl || '',
          }));
        } else if (tipo === 'fondos' && companyId) {
          todos = (await fetchFondosGuardados(companyId)).map((f) => ({
            id: String(f.id),
            label: f.nombre,
            thumbUrl: f.imageUrl || '',
            // El display viene transformado a miniatura: el original se firma al descargar.
            fileName: f.fileName,
          }));
        } else if (tipo === 'subidas' && companyId) {
          todos = await fotosSubidas(companyId, limite);
        }
      } catch (err) {
        console.warn('[MostrarGuardado] no pude traer', tipo, err);
      }
      if (!vivo) return;
      const visibles = filtrarGuardado(todos, buscar, limite);
      setHayEnTotal(todos.length > 0);
      setItems(visibles);
      setCargando(false);
      // Qué mostró y en qué orden — lo lee el `[registro:]` del próximo turno.
      chat.patchToolCard(card.id, {
        response: {
          shown: true,
          tipo,
          items: visibles.map((i) => ({ id: i.id, label: i.label })),
        },
      });
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, tipo, limite, buscar, productos, modelos]);

  // ── Descargar todo ───────────────────────────────────────────────────────
  const { chooserElement, resolverOpciones, aplicarOpciones } = useOpcionesDeDescarga();
  const [bajando, setBajando] = useState<null | { hechos: number; total: number }>(null);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [avisoDescarga, setAvisoDescarga] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuAbierto) return;
    const alClick = (ev: MouseEvent | TouchEvent) => {
      if (!menuRef.current?.contains(ev.target as Node)) setMenuAbierto(false);
    };
    document.addEventListener('mousedown', alClick);
    document.addEventListener('touchstart', alClick);
    return () => {
      document.removeEventListener('mousedown', alClick);
      document.removeEventListener('touchstart', alClick);
    };
  }, [menuAbierto]);

  /** La URL del ORIGINAL de cada item. Se resuelve recién acá: firmar los archivos
   *  grandes al mostrar la galería sería pagar por descargas que nadie pidió. */
  const archivosParaBajar = async (): Promise<ArchivoDeLote[]> => {
    if (!tipo) return [];
    const urls = await mapWithConcurrency(items, 4, async (item) => {
      if (item.fullUrl) return item.fullUrl;
      try {
        if (tipo === 'productos' && companyId) {
          return (await getProductFullImageUrl(companyId, item.id)) || item.thumbUrl;
        }
        if (tipo === 'fondos' && companyId && item.fileName) {
          return (await signFondoGuardadoFull(companyId, item.fileName)) || item.thumbUrl;
        }
      } catch {
        /* cae a la miniatura: peor calidad, pero baja */
      }
      return item.thumbUrl;
    });
    return items
      .map((item, i) => ({ url: urls[i] || '', nombre: nombreDeArchivo(tipo, item, i) }))
      .filter((a) => a.url);
  };

  const descargar = async (comoZip: boolean) => {
    if (bajando || !tipo) return;
    setMenuAbierto(false);
    setAvisoDescarga(null);
    // El formato de salida se pregunta UNA vez para todo el lote, antes de traer nada
    // (ADR-2026-09-14-configuracion-de-descarga). La marca de agua no: acá lo que se
    // baja es el material de referencia —productos, fondos, modelos—, no una creación.
    const opciones = await resolverOpciones({ cantidad: items.length, sinMarcaDeAgua: true });
    if (opciones === null) return; // cerró la hoja: cancelar es cancelar
    const transformar = (blob: Blob) => aplicarOpciones(blob, false, opciones);
    setBajando({ hechos: 0, total: items.length });
    try {
      const archivos = await archivosParaBajar();
      const onProgreso = (hechos: number, total: number) => setBajando({ hechos, total });
      const r = comoZip
        ? await descargarComoZip(archivos, nombreDelZip(tipo), onProgreso, transformar)
        : await descargarSueltos(archivos, onProgreso, transformar);
      if (r.ok === 0) setAvisoDescarga('No pude descargar ninguno. Probá de nuevo.');
      else if (r.fallidos > 0) setAvisoDescarga(`Bajé ${r.ok}; ${r.fallidos} fallaron.`);
    } catch (err) {
      console.warn('[MostrarGuardado] descarga', err);
      setAvisoDescarga('No pude completar la descarga.');
    }
    setBajando(null);
  };

  const abrir = (item: ItemGuardado) => {
    // Sólo los productos tienen una pantalla propia adonde ir. El resto se mira acá.
    if (tipo === 'productos') navigate(`/mi-marca?producto=${encodeURIComponent(item.id)}`);
  };

  // Sin tipo válido no hay nada que mostrar: el agente ya se enteró por el resultado.
  if (!tipo) return null;

  const { titulo } = ETIQUETA_TIPO[tipo];

  return (
    <div className="rounded-2xl border border-[#02062B]/[0.08] bg-white p-3">
      {/* La hoja del formato de salida. Se dibuja en un portal, así que el lugar en el
          árbol no importa; lo que importa es que esté montada mientras la card viva. */}
      {chooserElement}
      <div className="flex items-center justify-between gap-2 px-1 pb-2">
        <p className="text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
          {titulo}
        </p>
        {puedeDescargarTodo(items) && (
          <div ref={menuRef} className="relative shrink-0">
            {/* Botón partido: el cuerpo hace lo más común (un zip) y el chevron abre la
                otra forma. Así el caso frecuente es un solo click y el otro sigue a mano. */}
            <div className="inline-flex overflow-hidden rounded-xl bg-[#F0F2F7]">
              <button
                type="button"
                onClick={() => void descargar(true)}
                disabled={!!bajando}
                className="inline-flex items-center gap-1.5 py-1.5 px-3 text-[12.5px] font-medium text-[#02062B]/[0.75] transition-colors hover:bg-[#02062B]/[0.07] disabled:opacity-50"
              >
                {bajando ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {bajando ? `${bajando.hechos}/${bajando.total}` : 'Descargar todo'}
              </button>
              <button
                type="button"
                onClick={() => setMenuAbierto((v) => !v)}
                disabled={!!bajando}
                aria-label="Cómo descargar"
                aria-haspopup="menu"
                aria-expanded={menuAbierto}
                className="inline-flex items-center border-l border-[#02062B]/[0.08] py-1.5 px-1.5 text-[#02062B]/[0.6] transition-colors hover:bg-[#02062B]/[0.07] disabled:opacity-50"
              >
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
            {menuAbierto && (
              <div
                role="menu"
                className="absolute right-0 top-full z-20 mt-1 w-[220px] rounded-xl border border-[#02062B]/[0.06] bg-white p-1 shadow-xl"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void descargar(true)}
                  className="block w-full rounded-lg px-2.5 py-2 text-left text-[13px] text-brand-black/90 transition-colors hover:bg-neutral-50"
                >
                  Un archivo .zip
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => void descargar(false)}
                  className="block w-full rounded-lg px-2.5 py-2 text-left text-[13px] text-brand-black/90 transition-colors hover:bg-neutral-50"
                >
                  Archivos individuales
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {cargando ? (
        <div className="flex items-center gap-2 px-1 py-4 text-[13.5px] text-[#02062B]/[0.55]">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Buscando…
        </div>
      ) : items.length === 0 ? (
        <p className="flex items-center gap-2 px-1 py-3 text-[13.5px] text-[#02062B]/[0.55]">
          <ImageOff className="h-4 w-4 shrink-0" aria-hidden="true" />
          {buscar && hayEnTotal
            ? `No encontré nada que diga "${buscar}".`
            : ETIQUETA_TIPO[tipo].vacio}
        </p>
      ) : (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {items.map((item, i) => (
            <li key={`${item.id}-${i}`}>
              <button
                type="button"
                onClick={() => abrir(item)}
                disabled={tipo !== 'productos'}
                title={item.label || undefined}
                className={`block w-full text-left ${
                  tipo === 'productos' ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                <span
                  className="block w-full overflow-hidden rounded-xl bg-[#E9EAF0]"
                  style={{ aspectRatio: '4 / 5' }}
                >
                  {item.thumbUrl ? (
                    <CachedImg
                      src={item.thumbUrl}
                      alt={item.label || 'Guardado'}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="block h-full w-full" />
                  )}
                </span>
                {item.label && (
                  <span className="mt-1 block truncate px-0.5 text-[12px] text-[#02062B]/[0.6]">
                    {item.label}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {avisoDescarga && (
        <p role="status" className="px-1 pt-2 text-[12.5px] text-[#02062B]/[0.55]">
          {avisoDescarga}
        </p>
      )}
    </div>
  );
};

export default MostrarGuardadoCard;
